import { useCallback, useEffect, useState } from 'react';
import { Bell, CheckCircle, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useAnnouncementStore, useArchivedAnnouncement } from '../../store/announcementStore';
import { pickContent } from './AnnouncementBanner';
import { localizeTimes } from '../../utils/announcementTemplate';
import type { AnnouncementLevel } from '../../types';

const icons: Record<AnnouncementLevel, typeof Info> = {
  success: CheckCircle,
  info: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
};

/** Réduit le HTML d'une annonce à un aperçu texte pour la liste du menu. */
function toPlainText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

interface NotificationsMenuProps {
  /** Classes du bouton — chaque shell (topbar Méridien, en-tête classique) a les siennes. */
  buttonClassName?: string;
  className?: string;
}

/**
 * Menu des notifications : un bouton et un panneau, pas une page.
 *
 * Il recueille les annonces que l'utilisateur a fermées mais qui sont toujours
 * diffusées. Rouvrir une annonce la réaffiche en bandeau *le temps du menu* :
 * à la fermeture du panneau, l'aperçu se retire avec lui — c'est une
 * consultation, pas une réouverture durable.
 */
export default function NotificationsMenu({
  buttonClassName,
  className,
}: NotificationsMenuProps) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const archived = useArchivedAnnouncement();
  const { preview, endPreview, previewing } = useAnnouncementStore();

  // Fermer le panneau retire l'aperçu, quel que soit le chemin emprunté
  // (clic à l'extérieur, Échap, démontage) — sinon le bandeau resterait
  // affiché sans que rien ne permette de le refermer depuis le menu.
  const close = useCallback(() => {
    setOpen(false);
    endPreview();
  }, [endPreview]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  useEffect(() => endPreview, [endPreview]);

  const count = archived ? 1 : 0;

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className={clsx('relative', buttonClassName)}
        aria-label={t('announcement.notifications')}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t('announcement.notifications')}
      >
        <Bell className="w-[18px] h-[18px]" />
        {count > 0 && (
          <span
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary-500 ring-2 ring-[var(--color-bg-surface,#fff)]"
            aria-hidden="true"
          />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={close} />
          <div
            role="dialog"
            aria-label={t('announcement.notifications')}
            className="absolute right-0 z-20 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-base bg-surface shadow-lg py-1"
            style={{ backgroundColor: 'var(--color-bg-surface)' }}
          >
            <div className="px-4 py-2 border-b border-base">
              <p className="text-sm font-semibold text-base">
                {t('announcement.notifications')}
              </p>
            </div>

            {!archived && (
              <p className="px-4 py-6 text-sm text-base-muted text-center">
                {t('announcement.empty')}
              </p>
            )}

            {archived && (() => {
              const Icon = icons[archived.level];
              const language = i18n.language.startsWith('en') ? 'en' : 'fr';
              // Même conversion que dans le bandeau : l'aperçu du menu doit
              // annoncer le même horaire que le message qu'il rouvre.
              const html = localizeTimes(
                pickContent(i18n.language, archived.contentFr, archived.contentEn),
                language
              );

              return (
                <button
                  type="button"
                  onClick={() => {
                    preview();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={clsx(
                    'flex items-start gap-3 w-full px-4 py-3 text-left transition-colors hover:bg-surface-hover',
                    previewing && 'bg-surface-hover'
                  )}
                  aria-pressed={previewing}
                >
                  <span
                    className="announcement-dot mt-1.5 flex-shrink-0"
                    data-level={archived.level}
                    aria-hidden="true"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-base">
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                      {t(`announcement.level.${archived.level}`)}
                    </span>
                    <span className="text-sm text-base-secondary line-clamp-2 mt-0.5">
                      {toPlainText(html)}
                    </span>
                    <span className="block text-xs text-base-muted mt-1">
                      {previewing ? t('announcement.showing') : t('announcement.clickToReopen')}
                    </span>
                  </span>
                </button>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}
